/**
 * <PageHeader /> — locked layout for every page's title row.
 *
 * Per DESIGN_SYSTEM.md §12 "Page Composition Rules":
 *   - Breadcrumbs (one line, text-sm)
 *   - Page title (text-2xl)
 *   - Page actions (right-aligned: primary button + secondary buttons
 *     + overflow menu)
 *
 * Slots:
 *   - breadcrumbs: array of { label, href? }. Last item is the
 *     current page and is NOT a link.
 *   - actions: ReactNode rendered on the right. Caller composes the
 *     primary + secondary buttons; PageHeader doesn't prescribe the
 *     button shapes.
 *
 * Bilingual + RTL: title uses display font via the html lang
 * selector in tokens.css. Breadcrumb chevron flips via the RTL
 * variant on the icon's transform.
 */

import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: string;
  /** Optional one-line subtitle, rendered text-sm text-secondary. */
  subtitle?: string;
  /** Breadcrumb trail. Last entry's href is ignored; it's the current page. */
  breadcrumbs?: Breadcrumb[];
  /** Right-aligned actions slot. */
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actions,
  className = "",
}: PageHeaderProps): ReactNode {
  const { t } = useTranslation();
  return (
    <header
      className={["flex flex-col gap-2", className].filter(Boolean).join(" ")}
    >
      {breadcrumbs && breadcrumbs.length > 0 && (
        // aria-label distinct from the WorkspaceTopBar's "Breadcrumb"
        // nav so axe's landmark-unique check doesn't flag the two as
        // a duplicate landmark when both render on the same page.
        <nav
          aria-label={t("app.ph_breadcrumb_page", "Page breadcrumb")}
          className="text-sm"
        >
          <ol className="flex flex-wrap items-center gap-1">
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <li
                  key={`${crumb.label}-${i}`}
                  className="flex items-center gap-1"
                >
                  {i > 0 && (
                    <ChevronRight
                      aria-hidden="true"
                      size={14}
                      className="text-text-tertiary rtl:rotate-180"
                    />
                  )}
                  {isLast || !crumb.href ? (
                    <span
                      aria-current={isLast ? "page" : undefined}
                      className={
                        isLast
                          ? "text-text-secondary"
                          : "text-text-tertiary"
                      }
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      to={crumb.href}
                      className={[
                        "text-text-tertiary",
                        "hover:text-text-primary",
                        "transition-colors duration-micro ease-out",
                        "focus-visible:outline-none focus-visible:underline",
                      ].join(" ")}
                    >
                      {crumb.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-text-primary truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm text-text-secondary">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  );
}
